if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();
 
      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection && this.productSection.querySelector('.product-info');
      this.productForm = this.productSection && this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window) { 
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.richOnVariant && this.jsProduct) {
        this.jsProduct.removeEventListener('on:variant:change', this.richOnVariant);
      }
    }

    bindEvents() {
      if (this.dataset.stickyAtcRich === 'true') {
        this.richOnVariant = this.richOnVariant || this.onRichVariantChange.bind(this);
        this.jsProduct = this.productSection && this.productSection.querySelector('.js-product');
        if (this.jsProduct) {
          this.jsProduct.addEventListener('on:variant:change', this.richOnVariant);
          this.syncPriceFromMain();
          this.syncStickyCta();
          if (this.querySelector('.js-sticky-bar__atc') && 'requestAnimationFrame' in window) {
            requestAnimationFrame(() => {
              this.syncPriceFromMain();
              this.syncStickyCta();
            });
            setTimeout(() => {
              this.syncPriceFromMain();
              this.syncStickyCta();
            }, 400);
          }
        }
      }

      if (!('MutationObserver' in window)) {
        if (this.dataset.stickyAtcRich === 'true' && 'requestAnimationFrame' in window) {
          requestAnimationFrame(() => {
            this.syncPriceFromMain();
            this.syncStickyCta();
          });
        }
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }

    onRichVariantChange(evt) {
      const d = evt.detail;
      if (!d || !d.variant) return;

      const { variant, product: prod } = d;

      this.updateVariantImage(variant, prod);
      this.updateOptionLabels(d);

      if ('requestAnimationFrame' in window) {
        requestAnimationFrame(() => {
          this.syncPriceFromMain();
          this.syncStickyCta();
        });
        setTimeout(() => {
          this.syncPriceFromMain();
          this.syncStickyCta();
        }, 50);
      } else {
        this.syncPriceFromMain();
        this.syncStickyCta();
      }
    }

    /**
     * Set sticky thumbnail from variant featured image (or product fallback).
     * @param {object} variant
     * @param {object} [product]
     */
    updateVariantImage(variant, product) {
      const img = this.querySelector('.js-sticky-bar__img');
      if (!img) return;

      let url = null;
      const fi = variant.featured_image;
      if (fi && fi.src) {
        url = fi.src;
      } else if (product) {
        const pfi = product.featured_image || product.image;
        if (pfi && pfi.src) {
          url = pfi.src;
        }
      }

      if (url) {
        const mediaWrap = this.querySelector('.sticky-atc-bar__media');
        if (mediaWrap) {
          mediaWrap.removeAttribute('hidden');
        }
        const joiner = url.indexOf('?') > -1 ? '&' : '?';
        const sized = url.includes('width=') ? url : `${url}${joiner}width=120&crop=center`;
        if (sized !== img.src) {
          img.removeAttribute('srcset');
          img.sizes = '96px';
          img.src = sized;
        }
      } else {
        const mediaWrap = this.querySelector('.sticky-atc-bar__media');
        if (mediaWrap) {
          mediaWrap.setAttribute('hidden', '');
        }
      }
    }

    updateOptionLabels(detail) {
      const { variant } = detail;
      const options = (variant && variant.options) || [];

      this.querySelectorAll('.js-sticky-bar-opt').forEach((el) => {
        const i = parseInt(el.getAttribute('data-option-index') || '0', 10);
        if (Number.isNaN(i) || i < 0) return;
        if (options[i] !== undefined && options[i] !== null) {
          el.textContent = String(options[i]);
        }
      });
    }

    /**
     * Mirror the main product column price (same DOM as variant-picker / updatePrice).
     */
    syncPriceFromMain() {
      if (this.dataset.stickyAtcRich !== 'true') return;
      const dest = this.querySelector('.js-sticky-bar-price');
      if (!dest) return;
      if (!this.productInfo) {
        this.productInfo = this.productSection && this.productSection.querySelector('.product-info');
      }
      if (!this.productInfo) return;

      const src =
        this.productInfo.querySelector('.product-info__price')
        || this.productInfo.querySelector('#pdp-price-container .price, #pdp-price-container')
        || this.productInfo.querySelector('#pdp-price-container');
      if (!src) {
        if (this.jsProduct) {
          const p =
            this.jsProduct.querySelector('.product-info__price')
            || this.jsProduct.querySelector('#pdp-price-container');
          if (p) {
            this.mirrorPriceNode(dest, p);
            return;
          }
        }
        return;
      }
      this.mirrorPriceNode(dest, src);
    }

    /**
     * @param {Element} dest
     * @param {Element} src
     */
    mirrorPriceNode(dest, src) {
      const clone = src.cloneNode(true);
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => {
        el.removeAttribute('id');
      });
      dest.replaceChildren();
      dest.appendChild(clone);
    }

    /**
     * Match the main add-to-cart button: disabled + label (e.g. price from variant-picker).
     */
    syncStickyCta() {
      if (this.dataset.stickyAtcRich !== 'true') return;
      const sticky = this.querySelector('.js-sticky-bar__atc');
      if (!this.productSection) return;
      const main = this.productSection.querySelector(
        '.product-form--atc-button[name="add"]'
      );
      if (!sticky || !main) return;

      sticky.disabled = main.disabled;
      if (main.textContent && main.textContent.trim() !== '') {
        sticky.textContent = main.textContent;
      } else {
        const base = (main.dataset && main.dataset.addToCartText) || main.textContent;
        if (base) {
          sticky.textContent = base;
        }
      }
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
